import { QuantifyDurationDefinitionInfo } from '@modules/analysis/components/AnalysisDefinitionInfo/QuantifyDurationDefinitionInfo';
import { QuantifyFilterDefinitionInfo } from '@modules/analysis/components/AnalysisDefinitionInfo/QuantifyFilterDefinitionInfo';
import { EqlFieldCommonMetadataSourceIcons } from '@modules/common/components/EqlFieldCommonMetadataInfo';
import { getFormattedDisplayNameForEqlField } from '@modules/common/components/EqlFieldsAutoComplete/helpers';
import {
  useIsUnifiedFieldsEnabled,
  useShouldRemoveFileUploadPrefix,
} from '@modules/common/hooks';
import { MeasuredAsMetricDenominator } from '@modules/quantify/components/QuantifyQueryBuilder/Fields/MeasuredAsField/MeasuredAsMetricDenominator';
import {
  useGetMeasuredAsDisplayName,
  useIsAnomaliesTrendVisible,
} from '@modules/quantify/hooks';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { Box, Divider, Grid, Stack, Typography, alpha } from '@mui/material';
import { AnalysisDefinitionInfoItem } from './AnalysisDefinitionInfoItem';

interface AnalysisDefinitionInfoProps {
  shouldHideTooltips?: boolean;
}

export const AnalysisDefinitionInfo = ({
  shouldHideTooltips = false,
}: AnalysisDefinitionInfoProps) => {
  const groupBy = useQuantifyStore(({ query }) => query.groupBy);
  const compareTo = useQuantifyStore(({ query }) => query.compareTo);
  const isAnomaliesTrendVisible = useIsAnomaliesTrendVisible();
  const measuredAsDisplayName = useGetMeasuredAsDisplayName();
  const anomalousPrefix = isAnomaliesTrendVisible ? ' Anomalous: ' : '';
  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();
  const isUnifiedFieldEnabled = useIsUnifiedFieldsEnabled();

  const getShowMeLabel = () => {
    if (groupBy) {
      return (
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box>
            {anomalousPrefix}
            {getFormattedDisplayNameForEqlField({
              eqlField: groupBy,
              shouldRemoveFileUploadPrefix,
              isUnifiedFieldEnabled,
            })}
          </Box>
          <EqlFieldCommonMetadataSourceIcons
            eqlField={groupBy}
            shouldHideTooltip
          />
        </Stack>
      );
    }
    return '--';
  };

  const compareTitlePrefix =
    compareTo?.type === 'whereClause' ? 'Filter' : 'Duration';
  const compareTitle = compareTo
    ? ` ${compareTitlePrefix} Comparison`
    : undefined;

  return (
    <Stack spacing={1}>
      <Stack spacing={1} direction="row" alignItems="center">
        <Typography variant="caption" color="inherit">
          {compareTitle ?? 'Quantify'} Analysis
        </Typography>
      </Stack>
      <Divider />
      <Grid
        container
        alignItems="flex-start"
        rowSpacing={1}
        columnSpacing={0.5}
      >
        <AnalysisDefinitionInfoItem
          leftNode="Show me:"
          rightNode={getShowMeLabel()}
        />
        <QuantifyFilterDefinitionInfo shouldHideTooltips={shouldHideTooltips} />
        <QuantifyDurationDefinitionInfo />
        <AnalysisDefinitionInfoItem
          leftNode="Measured as:"
          rightNode={
            <Stack spacing={1}>
              <Box>{measuredAsDisplayName}</Box>
              <MeasuredAsMetricDenominator
                isReadOnly
                WhereClausePreviewProps={{
                  shouldHideTooltips: true,
                  sxProps: {
                    backgroundColor: (theme) =>
                      alpha(theme.palette.background.paper, 0.8),
                  },
                }}
              />
            </Stack>
          }
        />
      </Grid>
    </Stack>
  );
};
